import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Fuse from 'fuse.js'
import { PAGE_REGISTRY, type PageEntry } from '@/lib/pageRegistry'

const MAX_RECENT = 5
const STORAGE_KEY = 'qyngent-recent-pages'

function getRecentPages(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

function addRecentPage(path: string) {
  const recent = getRecentPages().filter((p) => p !== path)
  recent.unshift(path)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const navigate = useNavigate()

  const fuse = useMemo(
    () => new Fuse(PAGE_REGISTRY, {
      keys: ['title', 'keywords', 'group'],
      threshold: 0.4,
      includeScore: true,
    }),
    []
  )

  const results: PageEntry[] = useMemo(() => {
    if (!query.trim()) {
      const recentPaths = getRecentPages()
      const recent = recentPaths
        .map((p) => PAGE_REGISTRY.find((e) => e.path === p))
        .filter(Boolean) as PageEntry[]
      const actions = PAGE_REGISTRY.filter((e) => e.category === 'action')
      return [...recent, ...actions].slice(0, 10)
    }
    return fuse.search(query).map((r) => r.item).slice(0, 10)
  }, [query, fuse])

  const open = useCallback(() => { setIsOpen(true); setQuery(''); setSelectedIndex(0) }, [])
  const close = useCallback(() => { setIsOpen(false); setQuery('') }, [])

  const select = useCallback((entry: PageEntry) => {
    addRecentPage(entry.path)
    navigate(entry.path)
    close()
  }, [navigate, close])

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Arrow key navigation
  useEffect(() => {
    if (!isOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault()
        select(results[selectedIndex])
      } else if (e.key === 'Escape') {
        close()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, results, selectedIndex, select, close])

  // Reset selection when query changes
  useEffect(() => { setSelectedIndex(0) }, [query])

  return { isOpen, open, close, query, setQuery, results, selectedIndex, select }
}
